import React from 'react';
import type { ConversationData } from '../context/chatTypes';
import { shortenAddress, formatMessageTime, truncateMessage } from '../utils/chatHelpers';

interface ConversationItemProps {
  conversation: ConversationData;
  isActive: boolean;
  unreadCount: number;
  onClick: () => void;
}

const ConversationItem: React.FC<ConversationItemProps> = React.memo(
  ({ conversation, isActive, unreadCount, onClick }) => {
    const isHnsRegistered = !conversation.isGroup && !!conversation.peerDisplayName;
    const displayName = conversation.isGroup
      ? conversation.groupName || 'Group Chat'
      : isHnsRegistered
        ? (conversation.peerHnsDisplayName || conversation.peerDisplayName!)
        : shortenAddress(conversation.peerAddress);

    const displaySubtext = isHnsRegistered
      ? (conversation.peerHnsDisplayName ? conversation.peerDisplayName! : undefined)
      : undefined;

    const timeDisplay = conversation.lastMessageTime
      ? formatMessageTime(conversation.lastMessageTime)
      : formatMessageTime(conversation.createdAt);

    const preview = conversation.lastMessageText
      ? truncateMessage(conversation.lastMessageText)
      : 'No messages yet';

    const isUnread = unreadCount > 0;

    const tooltipText = conversation.isGroup
      ? conversation.groupName
      : isHnsRegistered
        ? `${conversation.peerHnsDisplayName || ''} ${conversation.peerDisplayName} (${conversation.peerAddress})`.trim()
        : conversation.peerAddress;

    return (
      <div
        className={`chat-conversation-item ${isActive ? 'chat-conversation-item--active' : ''} ${isUnread && !isActive ? 'chat-conversation-item--unread' : ''}`}
        onClick={onClick}
      >
        <div className="chat-conversation-item__avatar">
          <img
            className="chat-avatar"
            src={`https://api.dicebear.com/9.x/avataaars/svg?seed=${conversation.isGroup ? (conversation.groupName || conversation.id) : conversation.peerAddress}`}
            alt=""
          />
        </div>

        <div className="chat-conversation-item__content">
          <div className="chat-conversation-item__header">
            <div className="chat-conversation-item__name-block">
              <span className="chat-conversation-item__name" title={tooltipText}>
                {displayName}
              </span>
              {displaySubtext && (
                <span className="chat-conversation-item__username">{displaySubtext}</span>
              )}
            </div>
            <span className="chat-conversation-item__time">{timeDisplay}</span>
          </div>
          <div className="chat-conversation-item__preview">
            <span className="chat-conversation-item__message">{preview}</span>
            {unreadCount > 0 && (
              <span className="chat-conversation-item__badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </div>
        </div>
      </div>
    );
  }
);

ConversationItem.displayName = 'ConversationItem';

export default ConversationItem;
